import { useCallback, useEffect, useState } from 'react'
import { registry, UNIT_KINDS, type Unit } from '../lib/registry'

/**
 * The belt.
 *
 * Lanes are the lifecycle, left to right, and the order is deliberately the
 * order things actually move in. `dead` is a real lane and not a hidden flag:
 * five of these are explicitly not worth pursuing, and a list that cannot say so
 * keeps suggesting them back.
 */
const LANES = [
  { key: 'building', label: 'On the belt', hint: 'Being built now' },
  { key: 'planned',  label: 'Next up',     hint: 'Scheduled, in order' },
  { key: 'idea',     label: 'Waiting',     hint: 'Wants building' },
  { key: 'live',     label: 'Shipped',     hint: 'Out there' },
  { key: 'archived', label: 'Archive',     hint: 'Done with, kept for parts' },
  { key: 'dead',     label: 'Not pursuing', hint: 'Said so out loud' },
]

/**
 * A slug the API will accept, derived from the name.
 *
 * The server's rule is /^[a-z0-9][a-z0-9-]{1,40}$/ — so 2 to 41 characters,
 * starting alphanumeric. Deriving it here rather than making somebody invent
 * one is the difference between a form you use and a form you avoid, but it
 * stays EDITABLE: the slug is the permanent identifier and occasionally the
 * obvious derivation is not the one you want to live with.
 */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 41)
}

const BLANK = { name: '', slug: '', kind: 'nano-app', tagline: '', intent: '', lifecycle: 'idea' }

export default function Belt({ canWrite }: { canWrite: boolean }) {
  const [units, setUnits] = useState<Unit[]>([])
  const [open, setOpen] = useState<Unit | null>(null)
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState(BLANK)
  /* Whether the slug has been typed in. Once it has, the name stops driving it. */
  const [slugTouched, setSlugTouched] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => setUnits((await registry.units()).units), [])
  useEffect(() => { void load() }, [load])

  const slug = slugTouched ? draft.slug : slugify(draft.name)
  const slugOk = /^[a-z0-9][a-z0-9-]{1,40}$/.test(slug)

  function startAdd() {
    setDraft(BLANK); setSlugTouched(false); setError(null); setAdding(true)
  }

  async function create(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await registry.createUnit({
        slug,
        name: draft.name.trim(),
        kind: draft.kind,
        lifecycle: draft.lifecycle,
        tagline: draft.tagline.trim() || undefined,
        intent: draft.intent.trim() || undefined,
      })
      await load()
      setAdding(false)
    } catch (err) {
      /*
       * Matched on CODE, not on the message text. The one failure worth naming
       * precisely is the unique slug — the server answers 409 `slug_taken`, and
       * "something went wrong" would send you to the network tab to discover
       * you had already registered this thing. Reading the code rather than
       * grepping the prose means rewording the server message cannot silently
       * turn this back into a generic error.
       */
      const e = err as { code?: string; message?: string }
      setError(e?.code === 'slug_taken'
        ? `The slug “${slug}” is already in the registry.`
        : e?.message ?? 'Could not add it.')
    } finally {
      setBusy(false)
    }
  }

  async function move(unit: Unit, lifecycle: string) {
    setBusy(true)
    try { await registry.updateUnit(unit.slug, { lifecycle }); await load(); setOpen(null) }
    finally { setBusy(false) }
  }

  return (
    <section>
      {canWrite && (
        <div className="belt-head">
          <button className="primary add-unit" onClick={startAdd}>Add a unit</button>
        </div>
      )}

      <div className="belt">
        {LANES.map((lane) => {
          const mine = units.filter((u) => u.lifecycle === lane.key)
          return (
            <div key={lane.key} className="lane">
              <header>
                <h2>{lane.label} <span className="count">{mine.length}</span></h2>
                <p className="muted small">{lane.hint}</p>
              </header>
              {mine.map((u) => (
                <button key={u.id} className={`unit unit-${u.kind}`} onClick={() => setOpen(u)}>
                  <div className="unit-top">
                    {/* Priority is the queue position, so it leads. */}
                    {u.priority && <span className="pri">{u.priority}</span>}
                    <strong>{u.name}</strong>
                  </div>
                  {u.tagline && <span className="muted small">{u.tagline}</span>}
                  {u.blockers.length > 0 && (
                    <span className="blockers">needs {u.blockers.join(', ')}</span>
                  )}
                </button>
              ))}
              {mine.length === 0 && <p className="muted small empty">—</p>}
            </div>
          )
        })}
      </div>

      {adding && (
        <div className="sheet" role="dialog" aria-label="Add a unit">
          <div className="sheet-inner">
            <button className="link close" onClick={() => setAdding(false)}>close</button>
            <h2>Add a unit</h2>
            <p className="muted small">
              A unit is a thing the factory builds — an app, a site, a nano-app, a service,
              a course or a product. Components are the reusable pieces, and they are not added here.
            </p>

            <form onSubmit={create} className="unit-form">
              <label>
                <span>Name</span>
                <input
                  autoFocus
                  value={draft.name}
                  maxLength={120}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Ferry Pulse"
                  required
                />
              </label>

              <label>
                <span>Slug</span>
                <input
                  value={slug}
                  maxLength={41}
                  onChange={(e) => { setSlugTouched(true); setDraft({ ...draft, slug: e.target.value }) }}
                  placeholder="ferry-pulse"
                  aria-describedby="slug-hint"
                  required
                />
                <small id="slug-hint" className="muted">
                  {slug && !slugOk
                    ? '2–41 characters: lowercase letters, numbers and dashes, starting with a letter or number.'
                    : 'Permanent. Used in URLs and never changed once things point at it.'}
                </small>
              </label>

              <div className="row wrap">
                <label>
                  <span>Kind</span>
                  <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>
                    {UNIT_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </label>

                <label>
                  <span>Lane</span>
                  <select value={draft.lifecycle} onChange={(e) => setDraft({ ...draft, lifecycle: e.target.value })}>
                    {LANES.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                  </select>
                </label>
              </div>

              <label>
                <span>Tagline <em className="muted">optional</em></span>
                <input
                  value={draft.tagline}
                  maxLength={300}
                  onChange={(e) => setDraft({ ...draft, tagline: e.target.value })}
                  placeholder="What it is, in one line"
                />
              </label>

              <label>
                <span>Intent <em className="muted">optional</em></span>
                <textarea
                  value={draft.intent}
                  maxLength={2000}
                  rows={4}
                  onChange={(e) => setDraft({ ...draft, intent: e.target.value })}
                  placeholder="What it is FOR, in your own words. This one is worth writing now — it is the part nobody reconstructs later."
                />
              </label>

              {error && <p className="form-error" role="alert">{error}</p>}

              <div className="row">
                <button className="primary" type="submit" disabled={busy || !draft.name.trim() || !slugOk}>
                  {busy ? 'Adding…' : 'Add it'}
                </button>
                <button type="button" className="link" onClick={() => setAdding(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {open && (
        <div className="sheet" role="dialog" aria-label={open.name}>
          <div className="sheet-inner">
            <button className="link close" onClick={() => setOpen(null)}>close</button>
            <h2>{open.name}</h2>
            <p className="kind">{open.kind} · {open.lifecycle}{open.priority ? ` · queued ${open.priority}` : ''}</p>
            {open.intent && <p className="intent">{open.intent}</p>}
            {open.notes && <p className="muted note">{open.notes}</p>}

            {open.components.length > 0 && (
              <>
                <h3>Assembled from</h3>
                <ul className="pieces">
                  {open.components.map((c) => (
                    <li key={c.component_id} className={c.status === 'built' ? 'ok' : c.need === 'required' ? 'bad' : 'meh'}>
                      <strong>{c.name}</strong>
                      <span className="muted small">{c.need}{c.status !== 'built' ? ` · ${c.status}` : ''}</span>
                      {c.note && <span className="muted small">“{c.note}”</span>}
                    </li>
                  ))}
                </ul>
              </>
            )}

            <h3>Where it lives</h3>
            <ul className="wheres">
              {open.repo_path && <li><span className="muted small">repo</span><code>{open.repo_path}</code></li>}
              {open.live_url && <li><span className="muted small">live</span><a href={open.live_url} target="_blank" rel="noreferrer">{open.live_url}</a></li>}
              {open.source_path && <li><span className="muted small">source</span><code>{open.source_path}</code></li>}
              {!open.repo_path && !open.live_url && !open.source_path && <li className="muted">Nothing written down yet.</li>}
            </ul>

            {canWrite && (
              <>
                <h3>Move it</h3>
                <div className="row wrap">
                  {LANES.filter((l) => l.key !== open.lifecycle).map((l) => (
                    <button key={l.key} disabled={busy} onClick={() => move(open, l.key)}>{l.label}</button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
